import Util from '@/util'
import { isAnotherOverlayOpen } from '@/business-logic/course-conversion-discovery'

export const INK_DISCOVERY_STATE_FIELD = 'inkAnnotationsDiscovery'
export const INK_DISCOVERY_DOT_FLAG = 'PRE-169-presenter-ink-discovery-dot'

const MIN_HOSTED_SESSIONS = 3
const MAX_PRESENTING_SESSIONS_SEEN = 3
export const INK_DOT_LIFETIME_MS = 14 * 24 * 60 * 60 * 1000

export const CLEAR_REASONS = {
  CLICKED: 'clicked',
  SHORTCUT: 'shortcut',
  TIMED_OUT: 'timed_out',
  EXPIRED: 'expired',
}

const USED_REASONS = [CLEAR_REASONS.CLICKED, CLEAR_REASONS.SHORTCUT]

const SHOWN_EVENT = 'Ink > Discovery dot > Shown'
const CLEARED_EVENT = 'Ink > Discovery dot > Cleared'

const BELOW_THRESHOLD_KEY = 'inkDiscoveryBelowHostingThreshold'

export const hasEnoughHostingHistory = (hostedSessionCount) => (
  Number.isFinite(hostedSessionCount) && hostedSessionCount >= MIN_HOSTED_SESSIONS
)

export const readInkDiscoveryState = (personalInformation) => {
  const stored = personalInformation?.[INK_DISCOVERY_STATE_FIELD]
  const state = stored && typeof stored === 'object' ? stored : {}
  return {
    eligibleAt: state.eligibleAt || null,
    firstSeenAt: state.firstSeenAt || null,
    presentingSessionsSeen: Number.isFinite(state.presentingSessionsSeen) ? state.presentingSessionsSeen : 0,
    discoveredAt: state.discoveredAt || null,
    usedAt: state.usedAt || null,
  }
}

const toTime = (value) => {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

const stampOnce = (state, key, now) => ({
  ...state,
  [key]: state[key] || new Date(now).toISOString(),
})

export const isRetired = (state) => !!(state.discoveredAt || state.usedAt)

/** The reason a dot that was shown before should now be taken down, or null while it is still allowed to show. */
export const getRetirementReason = (state, now = Date.now()) => {
  if (!state.firstSeenAt) return null
  const firstSeenTime = toTime(state.firstSeenAt)
  if (firstSeenTime !== null && now - firstSeenTime >= INK_DOT_LIFETIME_MS) return CLEAR_REASONS.EXPIRED
  if (state.presentingSessionsSeen >= MAX_PRESENTING_SESSIONS_SEEN) return CLEAR_REASONS.TIMED_OUT
  return null
}

export const recordEligible = (state, now = Date.now()) => stampOnce(state, 'eligibleAt', now)

export const recordPresentingView = (state, now = Date.now()) => ({
  ...stampOnce(state, 'firstSeenAt', now),
  presentingSessionsSeen: state.presentingSessionsSeen + 1,
})

export const recordCleared = (state, reason, now = Date.now()) => {
  const discovered = stampOnce(state, 'discoveredAt', now)
  return USED_REASONS.includes(reason) ? stampOnce(discovered, 'usedAt', now) : discovered
}

export const canShowDotNow = ({ isPickerOpen, isInkToolActive, root }) => (
  !isPickerOpen && !isInkToolActive && !isAnotherOverlayOpen(root)
)

export const wasBelowHostingThresholdThisSession = (userId) => {
  try {
    return sessionStorage.getItem(BELOW_THRESHOLD_KEY) === String(userId)
  } catch (error) {
    return false
  }
}

export const markBelowHostingThresholdThisSession = (userId) => {
  try {
    sessionStorage.setItem(BELOW_THRESHOLD_KEY, String(userId))
  } catch (error) {
    // storage unavailable: the count is simply asked again next time
  }
}

export const trackDotShown = (trackingData = {}) => {
  Util.mixpanelTracking(trackingData.eventCategory, SHOWN_EVENT, null, null, { ...trackingData })
}

export const trackDotCleared = (reason, trackingData = {}) => {
  Util.mixpanelTracking(trackingData.eventCategory, CLEARED_EVENT, reason, null, { ...trackingData, reason })
}
